import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { IconAi, IconClose, IconWarning } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';

const SUGGESTIONS = [
  '今月の収支を分析して',
  '単価を上げるにはどうすればいい？',
  '確定申告の準備リストを教えて',
  '営業メールの文章を作って',
  'フリーランスの節税方法は？',
];

export default function AiAdvice() {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState('');
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { fetchContext(); }, []);

  // メッセージ追加時に自動スクロール
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const fetchContext = async () => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      setUserId(user.id);

      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const [invoiceRes, projectRes, expenseRes] = await Promise.all([
        supabase.from('invoices').select('*').eq('user_id', user.id),
        supabase.from('projects').select('*').eq('user_id', user.id),
        supabase.from('expenses').select('*').eq('user_id', user.id).gte('date', firstDay).lte('date', lastDay),
      ]);

      const invoices = invoiceRes.data || [];
      const projects = projectRes.data || [];
      const expenses = expenseRes.data || [];

      const monthIncome = invoices
        .filter(i => i.status === '\u5165\u91d1\u6e08\u307f' && i.issued_date >= firstDay && i.issued_date <= lastDay)
        .reduce((s, i) => s + (i.amount || 0), 0);
      const pendingAmount = invoices
        .filter(i => i.status === '\u672a\u5165\u91d1' || i.status === '\u5165\u91d1\u5f85\u3061')
        .reduce((s, i) => s + (i.amount || 0), 0);
      const monthExpense = expenses.reduce((s, e) => s + (e.amount || 0), 0);
      const activeProjects = projects.filter(p => p.status === '\u9032\u884c\u4e2d').length;

      setContext(`ユーザーの現在のデータ：
- 今月の確定収入: ${monthIncome.toLocaleString()}円
- 入金待ち金額: ${pendingAmount.toLocaleString()}円
- 今月の経費: ${monthExpense.toLocaleString()}円
- 進行中の案件数: ${activeProjects}件
- 総案件数: ${projects.length}件
- 総請求書数: ${invoices.length}件`);
    } catch (e) {
      console.error('context fetch error:', e);
    }
  };

  const sendMessage = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;

    setError('');
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const headers = { 'Content-Type': 'application/json' };

      // ユーザーIDをヘッダーに付与（レート制限の識別に使用）
      if (userId) headers['X-User-Id'] = userId;

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          system: `あなたはフリーランス・副業者専用のビジネスアドバイザーです。「Shigoto Note」というアプリのAIアシスタントとして、ユーザーの仕事やお金の悩みに実践的なアドバイスをします。回答は日本語で、簡潔かつ具体的に答えてください。\n\n${context}`,
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      // ── レート制限エラー（429）──
      if (response.status === 429) {
        const data = await response.json();
        const msg = data.message || '利用制限に達しました。しばらく後でお試しください。';
        setError(msg);
        // ユーザーメッセージをロールバック
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      if (!response.ok) {
        throw new Error('AIとの通信でエラーが発生しました。');
      }

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      const reply = data.content?.[0]?.text || '応答を取得できませんでした。';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setError(e.message);
      // ユーザーメッセージをロールバック
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const s = {
    card: { background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 14 },
  };

  return (
    <div style={{ padding: '20px 16px', maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', overflowX: 'hidden' }}>

      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, margin: 0 }}><IconAi size={18}/> AIアドバイス</h1>
        <p style={{ fontSize: 13, color: theme.textMuted, margin: '4px 0 0' }}>あなたのデータをもとにアドバイスします</p>
      </div>

      {messages.length === 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 8 }}>よく使う質問：</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SUGGESTIONS.map(s => (
              <button key={s} onClick={() => sendMessage(s)}
                style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 20, padding: '6px 14px', fontSize: 12, color: theme.textSecondary, cursor: 'pointer' }}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* エラーバナー */}
      {error && (
        <div style={{
          background: theme.dangerBg, border: '1px solid #7f1d1d', borderRadius: 10,
          padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ fontSize: 13, color: theme.dangerText }}><IconWarning size={14}/> {error}</span>
          <button onClick={() => setError('')}
            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
            ✕
          </button>
        </div>
      )}

      <div style={{ ...s.card, flex: 1, overflowY: 'auto', padding: '16px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textWeak }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}><IconAi size={18}/></div>
            <p style={{ fontSize: 14 }}>何でも聞いてください！</p>
            <p style={{ fontSize: 12, marginTop: 4 }}>あなたのデータをもとに分析します</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '80%', padding: '10px 14px', borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap',
              background: m.role === 'user' ? theme.primary : theme.bgInput,
              color: m.role === 'user' ? theme.primaryText : theme.textPrimary,
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ background: theme.bgInput, padding: '10px 16px', borderRadius: '14px 14px 14px 4px' }}>
              <span style={{ color: theme.textMuted, fontSize: 14 }}>考え中...</span>
            </div>
          </div>
        )}
        {/* 自動スクロール用アンカー */}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="メッセージを入力..."
          style={{ flex: 1, background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 12, padding: '12px 16px', fontSize: 14, color: theme.textPrimary, outline: 'none' }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          style={{ background: input.trim() && !loading ? theme.primary : theme.bgHover, color: input.trim() && !loading ? theme.primaryText : theme.textWeak, border: 'none', borderRadius: 12, padding: '12px 18px', fontSize: 14, fontWeight: 600, cursor: input.trim() && !loading ? 'pointer' : 'default' }}
        >
          送信
        </button>
      </div>
    </div>
  );
}
